import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import {
  FormulaAcquisitionParameter,
  FormulaConstant,
  FormulaInputParameter,
  FormulaMeasure,
  FormulaParameter,
  ParameterTypes,
} from '../../../types/formula.interface';
import { Measure } from '../../../types/measure.interface';
import { Unit } from '../../../types/unit.interface';
import { formatUnitLabel } from '../../../utils/unit-select.utils';
import { ParameterMode, SHOW_INPUT_DEFAULT_VALUE } from '../inputs/parameters-input/parameter-types';
import { hasUnitField } from '../inputs/parameters-input/parameter-utils';

@Component({
  selector: 'ces-parameter-display-row',
  standalone: true,
  imports: [TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './parameter-display-row.component.html',
  styles: `
    :host {
      display: contents;
    }
  `,
})
export class ParameterDisplayRowComponent {
  @Input() parameter!: FormulaParameter;
  @Input() index!: number;
  @Input() types: { label: string; value: string }[] = [];
  @Input() units: Unit[] = [];
  @Input() measures: Measure[] = [];
  @Input() mode: ParameterMode = ParameterMode.FORMULA_MODEL;

  ParameterMode = ParameterMode;
  protected readonly SHOW_INPUT_DEFAULT_VALUE = SHOW_INPUT_DEFAULT_VALUE;

  isConstant(): boolean {
    return this.parameter.type === ParameterTypes.CONSTANT;
  }

  isInput(): boolean {
    return this.parameter.type === ParameterTypes.INPUT;
  }

  isMeasure(): boolean {
    return this.parameter.type === ParameterTypes.MEASURE;
  }

  getUnitLabel(): string {
    if (!this.shouldShowUnit()) return '-';
    const param = this.parameter as FormulaConstant | FormulaInputParameter | FormulaAcquisitionParameter;
    const unit = this.units.find(u => u.id === param.unitId);
    return unit ? formatUnitLabel(unit.name, unit.symbol) : '-';
  }

  getMeasureLabel(): string {
    if (this.isMeasure()) {
      const param = this.parameter as FormulaMeasure;
      const measure = this.measures.find(m => m.id === param.measureId);
      return measure ? measure.name : '#' + param.measureId;
    }
    return '-';
  }

  getConstantValue(): string {
    if (this.isConstant()) {
      const param = this.parameter as FormulaConstant;
      return param.value !== null && param.value !== undefined ? param.value.toString() : '-';
    }
    return '-';
  }

  getDefaultValue(): string | number {
    if (this.isInput()) {
      const param = this.parameter as FormulaInputParameter;
      return param.defaultValue ?? '';
    }
    return '';
  }

  hasDefaultValue(): boolean {
    if (this.isInput()) {
      const param = this.parameter as FormulaInputParameter;
      return param.defaultValue !== null && param.defaultValue !== undefined;
    }
    return false;
  }

  shouldShowUnit(): boolean {
    if (!this.parameter) return false;
    return hasUnitField(this.parameter.type);
  }

  shouldShowMeasureSelect(): boolean {
    if (!this.parameter) return false;
    return (
      this.parameter.type === ParameterTypes.MEASURE && (this.mode === ParameterMode.FORMULA || this.mode === ParameterMode.POLYNOMIAL)
    );
  }

  getTypeLabel(typeValue: string): string {
    const typeObject = this.types.find(t => t.value === typeValue);
    return typeObject ? typeObject.label : typeValue;
  }
}
